/** Secondary-connectivity review queue dashboard (Phase 3 Stage 4, plan steps
 *  15 + 18). The small ambiguous bucket (tier=review / unrooted / meter-majority
 *  vs nearest-transformer disagreement) where the limited human effort goes.
 *
 *  Each row is actionable: ACCEPT the suggested derived root, REASSIGN to another
 *  candidate transformer, or MARK FOR GIS (record the decision for the feedback
 *  loop without re-rooting). Accept/reassign POST to the accept endpoint
 *  (Phase 5.1): the component's meters are re-parented (writes
 *  meters.transformer_id) AND a manual_connectivity_override is recorded for
 *  the read-time overlay, in one transaction; the row is optimistically
 *  removed from the queue. A row with no feeder_id (accept needs the feeder to
 *  validate the current run) falls back to the override-only path, as does
 *  MARK FOR GIS — it records a decision without moving meters, on purpose.
 *
 *  A compact, read-only COVERAGE SUMMARY (derived from the queue's why_flagged /
 *  tier grouping plus the active-override count) gives the GIS-quality picture
 *  without a separate endpoint. Everything degrades to an empty state until the
 *  gated prod compute runs (the derived tables are empty offline).
 *
 *  Mirrors the DiscrepancyDashboard layout + the engineering/discrepancy auth'd
 *  mutation patterns. */

import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { appPath } from '../../appConfig.ts'
import { useQuery } from '@tanstack/react-query'
import { getFeeders } from '../../api/topology.ts'
import { sortByLabel } from '../../utils/feederSort.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  useAcceptComponent,
  useCreateOverride,
  useDeactivateOverride,
  useOverrides,
  useReviewQueue,
} from '../../hooks/useSecondaryReview.ts'
import { TierBadge } from '../details/SecondaryConnectionSection.tsx'
import { WHY_FLAGGED_LABELS } from '../../types/secondaryReview.ts'
import { secondaryReviewMapLink } from '../../utils/secondaryReviewLink.ts'
import { useAuth } from '../../hooks/useAuth.ts'
import { hasCapability, capabilityTooltip } from '../../utils/capabilities.ts'
import type {
  CandidateTransformer,
  OverrideResponse,
  ReviewQueueItem,
} from '../../types/secondaryReview.ts'

const PAGE_SIZE = 50

// =============================================================================
// Coverage summary (read-only) — derived from the queue + override counts
// =============================================================================

function CoverageSummary({
  total,
  byReason,
  activeOverrides,
}: {
  total: number
  byReason: Record<string, number>
  activeOverrides: number
}) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Needs Review</div>
        <div className="text-2xl font-bold text-amber-600 mt-1">{total}</div>
        <div className="text-xs text-gray-400 mt-1">ambiguous components</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Unrooted</div>
        <div className="text-2xl font-bold text-red-600 mt-1">
          {byReason.unrooted ?? 0}
        </div>
        <div className="text-xs text-gray-400 mt-1">meters, no root</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Low Confidence</div>
        <div className="text-2xl font-bold text-amber-600 mt-1">
          {byReason.low_confidence ?? 0}
        </div>
        <div className="text-xs text-gray-400 mt-1">below auto-accept</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Disagreements</div>
        <div className="text-2xl font-bold text-purple-600 mt-1">
          {byReason.majority_disagrees_nearest ?? 0}
        </div>
        <div className="text-xs text-gray-400 mt-1">majority vs nearest</div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="text-xs text-gray-500 uppercase tracking-wide">Active Overrides</div>
        <div className="text-2xl font-bold text-blue-600 mt-1">{activeOverrides}</div>
        <div className="text-xs text-gray-400 mt-1">manual decisions</div>
      </div>
    </div>
  )
}

// =============================================================================
// Why-flagged badge
// =============================================================================

function WhyFlaggedBadge({ why }: { why: string }) {
  const meta = WHY_FLAGGED_LABELS[why]
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
      <span className={`w-2 h-2 rounded-full ${meta?.dot ?? 'bg-gray-400'}`} />
      {meta?.label ?? why.replace(/_/g, ' ')}
    </span>
  )
}

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '--'
  return `${Math.round(v * 100)}%`
}

// =============================================================================
// Candidate transformer chips (accept / reassign targets)
// =============================================================================

function candidateLabel(c: CandidateTransformer): string {
  const name = c.transformer_name || `#${c.transformer_id}`
  if (c.source === 'meter_majority' && c.meter_count != null) {
    return `${name} (${c.meter_count} mtr)`
  }
  return name
}

// =============================================================================
// Review queue table
// =============================================================================

function ReviewQueueTable({
  items,
  pendingId,
  canOverride,
  onAssign,
  onMarkGis,
}: {
  items: ReviewQueueItem[]
  pendingId: number | null
  canOverride: boolean
  onAssign: (item: ReviewQueueItem, transformerId: number) => void
  onMarkGis: (item: ReviewQueueItem) => void
}) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        No components need review. The queue is empty until a published
        connectivity run exists for a feeder (run the gated prod compute), and
        well-rooted feeders contribute nothing here.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th className="p-2">Component</th>
            <th className="p-2">Feeder</th>
            <th className="p-2">Why Flagged</th>
            <th className="p-2">Derived Root</th>
            <th className="p-2 text-right">Meters</th>
            <th className="p-2 text-right">Majority</th>
            <th className="p-2">Reassign / Accept</th>
            <th className="p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            // The first candidate is the suggested root (derived_root, when set).
            const suggested =
              item.candidates.find(c => c.source === 'derived_root') ??
              item.candidates[0]
            const busy = pendingId === item.component_id
            // Derived root, else the suggested candidate — same preference as
            // `suggested` above (the helper re-implements it as a pure fn).
            const mapLink = secondaryReviewMapLink(item)
            return (
              <tr
                key={`${item.run_id}-${item.component_id}`}
                className="border-b border-gray-100 hover:bg-gray-50 align-top"
              >
                <td className="p-2 font-mono text-xs whitespace-nowrap">
                  #{item.component_id}
                  <div className="text-[10px] text-gray-400">run {item.run_id}</div>
                </td>
                <td className="p-2 text-xs max-w-[140px] truncate" title={item.feeder_name || ''}>
                  {item.feeder_name || (item.feeder_id != null ? `#${item.feeder_id}` : '--')}
                </td>
                <td className="p-2 whitespace-nowrap">
                  <WhyFlaggedBadge why={item.why_flagged} />
                </td>
                <td className="p-2 text-xs whitespace-nowrap">
                  {item.transformer_id != null ? (
                    <span className="flex items-center gap-1.5">
                      #{item.transformer_id}
                      <TierBadge tier={item.tier} />
                    </span>
                  ) : (
                    <span className="text-red-500">unrooted</span>
                  )}
                  {item.root_method && (
                    <div className="text-[10px] text-gray-400">
                      {item.root_method.replace(/_/g, ' ')} {formatPct(item.root_confidence)}
                    </div>
                  )}
                </td>
                <td className="p-2 text-right text-xs">{item.meter_count}</td>
                <td className="p-2 text-right text-xs">{formatPct(item.majority_share)}</td>
                <td className="p-2">
                  <div className="flex flex-wrap gap-1 max-w-[260px]">
                    {item.candidates.length === 0 && (
                      <span className="text-xs text-gray-400">no candidates</span>
                    )}
                    {item.candidates.map(c => {
                      const isSuggested = c.transformer_id === suggested?.transformer_id
                      return (
                        <button
                          key={`${c.transformer_id}-${c.source}`}
                          onClick={() => onAssign(item, c.transformer_id)}
                          disabled={busy || !canOverride}
                          title={
                            !canOverride
                              ? capabilityTooltip('gis.override')
                              : isSuggested
                                ? 'Accept the suggested root'
                                : `Reassign to ${candidateLabel(c)} (${c.source})`
                          }
                          className={`text-xs px-2 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                            isSuggested
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                          }`}
                        >
                          {isSuggested ? 'Accept ' : ''}
                          {candidateLabel(c)}
                        </button>
                      )
                    })}
                  </div>
                </td>
                <td className="p-2">
                  <div className="flex flex-col items-start gap-1">
                    {mapLink ? (
                      <Link
                        to={appPath(mapLink)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                        title="Show this component's transformer on the map"
                      >
                        Show on map
                      </Link>
                    ) : (
                      <span
                        className="text-xs text-gray-400 whitespace-nowrap"
                        title="No derived root or candidate transformer on record — nothing locates this component on the map yet"
                      >
                        No map target
                      </span>
                    )}
                    <button
                      onClick={() => onMarkGis(item)}
                      disabled={busy || !canOverride || item.transformer_id == null}
                      title={
                        !canOverride
                          ? capabilityTooltip('gis.override')
                          : item.transformer_id == null
                            ? 'Marking pins the current root for the GIS team — this row has no root yet. Accept or reassign a transformer first.'
                            : "Record a 'send to GIS' decision (no re-root) so it is tracked for the GIS feedback loop."
                      }
                      className="text-xs px-2 py-1 rounded bg-gray-50 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Mark for GIS
                    </button>
                    {item.transformer_id == null && (
                      <span className="text-[10px] text-gray-400 max-w-[130px]">
                        Needs a root first — accept or reassign a transformer
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// =============================================================================
// Active overrides list (deactivate path)
// =============================================================================

function OverridesList({
  overrides,
  onDeactivate,
  pendingId,
  canOverride,
}: {
  overrides: OverrideResponse[]
  onDeactivate: (id: number) => void
  pendingId: number | null
  canOverride: boolean
}) {
  const active = overrides.filter(o => o.is_active)
  if (active.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">
        No active overrides.
      </div>
    )
  }
  return (
    <div className="space-y-2">
      {active.map(o => (
        <div
          key={o.id}
          className="p-3 rounded-lg border border-gray-200 bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">
              {o.source_type} #{o.source_id} &rarr; xfmr #{o.target_id}
            </span>
            <button
              onClick={() => onDeactivate(o.id)}
              disabled={pendingId === o.id || !canOverride}
              title={!canOverride ? capabilityTooltip('gis.override') : undefined}
              className="text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Undo
            </button>
          </div>
          <div className="text-[10px] text-gray-400 mt-1">
            {o.method}
            {o.reason ? ` — ${o.reason}` : ''}
            {o.created_by ? ` · by ${o.created_by}` : ''}
          </div>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// Main dashboard
// =============================================================================

export function SecondaryReviewDashboard() {
  const [feederId, setFeederId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  // Optimistic hide ONLY (UX nicety): rows resolved this session, keyed by
  // `run_id-component_id` (component_id is unique only within a run). The backend
  // now excludes override-resolved components from the queue persistently (P1-b),
  // so resolved rows stay gone after reload even without this set.
  const [resolved, setResolved] = useState<Set<string>>(new Set())
  const [pendingComponent, setPendingComponent] = useState<number | null>(null)
  const [pendingOverride, setPendingOverride] = useState<number | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // Overrides now require gis.override server-side (UX C5, spec 3.12) —
  // controls stay visible but disabled with the capability tooltip.
  const { user } = useAuth()
  const canOverride = hasCapability(user?.capabilities, 'gis.override')

  const { data: feeders } = useQuery({
    queryKey: ['feeders', 'all'],
    queryFn: () => getFeeders({ limit: 500 }),
    staleTime: 300_000,
  })

  const { data: queue, isLoading: loadingQueue } = useReviewQueue({
    feeder_id: feederId,
    skip: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  })

  const { data: overrides } = useOverrides({ active_only: true })

  const createMutation = useCreateOverride()
  const acceptMutation = useAcceptComponent()
  const deactivateMutation = useDeactivateOverride()

  // Filter out optimistically-resolved rows for the current page (keyed by
  // run_id-component_id to match the row key; the server exclusion is the
  // source of truth after the queue refetches).
  const visibleItems = useMemo(
    () =>
      (queue?.items ?? []).filter(
        i => !resolved.has(`${i.run_id}-${i.component_id}`),
      ),
    [queue, resolved],
  )

  const byReason = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const i of queue?.items ?? []) {
      counts[i.why_flagged] = (counts[i.why_flagged] ?? 0) + 1
    }
    return counts
  }, [queue])

  const handleAssign = useCallback(
    (item: ReviewQueueItem, transformerId: number) => {
      setActionError(null)
      setPendingComponent(item.component_id)

      if (item.feeder_id == null) {
        // Accept needs the feeder to validate the current run (Phase 5.1) --
        // a row with no feeder_id can't call the accept endpoint, so it keeps
        // recording a component-level override the old way (read-time
        // overlay only; feeder_id + anchor_segment_id scope the override and
        // give it a stable cross-run re-apply key, P1-a).
        createMutation.mutate(
          {
            source_type: 'component',
            source_id: item.component_id,
            target_id: transformerId,
            feeder_id: item.feeder_id,
            anchor_segment_id: item.anchor_segment_id,
            method: 'manual_review',
          },
          {
            onSuccess: () => {
              setResolved(prev =>
                new Set(prev).add(`${item.run_id}-${item.component_id}`),
              )
              setPendingComponent(null)
            },
            onError: err => {
              setActionError(friendlyError(err, 'apply the override'))
              setPendingComponent(null)
            },
          },
        )
        return
      }

      // Accept/reassign (Phase 5.1): re-parents the component's meters
      // (writes meters.transformer_id) and records the override in one
      // transaction. anchor_segment_id is the import-stable re-apply key the
      // server resolves the component by; a 409 names the stale run/page or
      // the meters that changed underneath this Accept.
      acceptMutation.mutate(
        {
          run_id: item.run_id,
          feeder_id: item.feeder_id!,
          component_id: item.component_id,
          anchor_segment_id: item.anchor_segment_id,
          root_transformer_id: transformerId,
        },
        {
          onSuccess: () => {
            setResolved(prev =>
              new Set(prev).add(`${item.run_id}-${item.component_id}`),
            )
            setPendingComponent(null)
          },
          onError: err => {
            setActionError(friendlyError(err, 'accept the component'))
            setPendingComponent(null)
          },
        },
      )
    },
    [createMutation, acceptMutation],
  )

  const handleMarkGis = useCallback(
    (item: ReviewQueueItem) => {
      // "Mark for GIS" records a tracked decision without changing the root: we
      // pin the component to its current derived root with method='send_to_gis'.
      // Unrooted rows can't reach here — the button is DISABLED with an inline
      // reason (UX Stage 1 C1); this guard remains as the TS narrowing for
      // `target_id` and as defense against a stale render.
      if (item.transformer_id == null) {
        setActionError(
          'Cannot mark an unrooted component for GIS — reassign a transformer or escalate via the coverage report.',
        )
        return
      }
      setActionError(null)
      setPendingComponent(item.component_id)
      createMutation.mutate(
        {
          source_type: 'component',
          source_id: item.component_id,
          target_id: item.transformer_id,
          feeder_id: item.feeder_id,
          anchor_segment_id: item.anchor_segment_id,
          method: 'send_to_gis',
          reason: 'Flagged for GIS investigation from review queue',
        },
        {
          onSuccess: () => {
            setResolved(prev =>
              new Set(prev).add(`${item.run_id}-${item.component_id}`),
            )
            setPendingComponent(null)
          },
          onError: err => {
            setActionError(friendlyError(err, 'mark the component for GIS'))
            setPendingComponent(null)
          },
        },
      )
    },
    [createMutation],
  )

  const handleDeactivate = useCallback(
    (id: number) => {
      setActionError(null)
      setPendingOverride(id)
      deactivateMutation.mutate(id, {
        onSettled: () => setPendingOverride(null),
        onError: err =>
          setActionError(friendlyError(err, 'undo the override')),
      })
    },
    [deactivateMutation],
  )

  const total = queue?.total ?? 0

  return (
    <div className="flex-1 overflow-auto p-6 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Secondary Connectivity Review
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Resolve the small bucket of ambiguous transformer&rarr;secondary
              rootings. Accept the suggestion or reassign to move the meters
              immediately, or flag for GIS to record the decision without
              re-rooting.
            </p>
          </div>

          {/* Feeder filter */}
          <select
            value={feederId ?? ''}
            onChange={e => {
              setFeederId(e.target.value ? Number(e.target.value) : null)
              setPage(0)
            }}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700"
          >
            <option value="">All Feeders</option>
            {feeders && sortByLabel(feeders, f => f.name || `Feeder #${f.id}`).map(f => (
              <option key={f.id} value={f.id}>
                {f.name || `Feeder #${f.id}`}
              </option>
            ))}
          </select>
        </div>

        <CoverageSummary
          total={total}
          byReason={byReason}
          activeOverrides={overrides?.length ?? 0}
        />

        {actionError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {actionError}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: active overrides */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Active Overrides
              </h3>
              <OverridesList
                overrides={overrides ?? []}
                onDeactivate={handleDeactivate}
                pendingId={pendingOverride}
                canOverride={canOverride}
              />
            </div>
          </div>

          {/* Right: review queue */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">
                  Review Queue
                  <span className="text-gray-400 ml-1">({total})</span>
                </h3>
              </div>

              {loadingQueue ? (
                <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>
              ) : (
                <>
                  <ReviewQueueTable
                    items={visibleItems}
                    pendingId={pendingComponent}
                    canOverride={canOverride}
                    onAssign={handleAssign}
                    onMarkGis={handleMarkGis}
                  />

                  {/* Pagination — only when there is more than one page. */}
                  {total > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-500">
                        Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}
                      </span>
                      <button
                        onClick={() => setPage(p => p + 1)}
                        disabled={(page + 1) * PAGE_SIZE >= total}
                        className="text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
